import moment from 'moment-timezone'

export const QILIN_PROGRESSION_CONFIG = Object.freeze({
  xpPerCheckin: 10,
  xpPerPlay: 5,
  levelUpCoin: 2,
  evolutionBonusCoin: 8,
  levelsPerStage: 5,
  maxStage: 7,
  maxLevel: 31,
  bondStepXp: 2000,
  renameCost: 5
})

export function xpToNext (level) {
  return 100 + 50 * (Math.max(1, Number(level) || 1) - 1)
}

export function totalXpForLevel (level) {
  let total = 0
  for (let current = 1; current < level; current += 1) total += xpToNext(current)
  return total
}

export function stageForLevel (level) {
  const safeLevel = Math.max(1, Number(level) || 1)
  return Math.min(
    QILIN_PROGRESSION_CONFIG.maxStage,
    1 + Math.floor((safeLevel - 1) / QILIN_PROGRESSION_CONFIG.levelsPerStage)
  )
}

export function levelForXp (xpValue) {
  const maxXp = totalXpForLevel(QILIN_PROGRESSION_CONFIG.maxLevel)
  const xp = Math.max(0, Math.min(maxXp, Number(xpValue) || 0))
  let level = 1
  while (level < QILIN_PROGRESSION_CONFIG.maxLevel && xp >= totalXpForLevel(level + 1)) level += 1
  return level
}

export function moodFromLastFed (lastFedDate, today) {
  if (!lastFedDate) return 'new'
  const days = moment(today, 'YYYY-MM-DD').diff(moment(lastFedDate, 'YYYY-MM-DD'), 'days')
  if (days <= 0) return 'happy'
  if (days === 1) return 'content'
  if (days <= 3) return 'hungry'
  return 'sulking'
}

export function createQilinPet () {
  return {
    name: '',
    gender: '',
    xp: 0,
    level: 1,
    bondXp: 0,
    feedCount: 0,
    lastFedDate: '',
    lastPlayedDate: '',
    levelRewardsPaid: {}
  }
}

export function applyQilinXp (petValue, xpGainValue, options = {}) {
  const pet = Object.assign(createQilinPet(), petValue || {})
  const maxXp = totalXpForLevel(QILIN_PROGRESSION_CONFIG.maxLevel)
  const currentXp = Math.max(0, Math.min(maxXp, Number(pet.xp) || 0))
  const currentLevel = levelForXp(currentXp)
  let remaining = Math.max(0, Number(xpGainValue) || 0)
  const xpApplied = Math.min(Math.max(0, maxXp - currentXp), remaining)
  const nextXp = currentXp + xpApplied
  remaining -= xpApplied

  let level = currentLevel
  let levelUps = 0
  let coinBonus = 0
  const rewardsPaid = Object.assign({}, pet.levelRewardsPaid || {})
  while (level < QILIN_PROGRESSION_CONFIG.maxLevel && nextXp >= totalXpForLevel(level + 1)) {
    const previousStage = stageForLevel(level)
    level += 1
    levelUps += 1
    let bonus = QILIN_PROGRESSION_CONFIG.levelUpCoin
    if (stageForLevel(level) > previousStage) bonus += QILIN_PROGRESSION_CONFIG.evolutionBonusCoin
    if (!rewardsPaid[String(level)]) {
      rewardsPaid[String(level)] = bonus
      coinBonus += bonus
    }
  }

  const next = Object.assign({}, pet, {
    xp: nextXp,
    level,
    bondXp: Math.max(0, Number(pet.bondXp) || 0) + remaining,
    levelRewardsPaid: rewardsPaid
  })
  if (options.feed) {
    next.feedCount = Math.max(0, Number(pet.feedCount) || 0) + 1
    next.lastFedDate = options.today || ''
  }
  return { next, levelUps, coinBonus, bondXpGained: remaining }
}

export function qilinPetSummary (petValue, today) {
  const pet = Object.assign(createQilinPet(), petValue || {})
  const maxXp = totalXpForLevel(QILIN_PROGRESSION_CONFIG.maxLevel)
  const xp = Math.max(0, Math.min(maxXp, Number(pet.xp) || 0))
  const level = levelForXp(xp)
  const isMaxLevel = level >= QILIN_PROGRESSION_CONFIG.maxLevel
  const bondXp = Math.max(0, Number(pet.bondXp) || 0)
  return {
    level,
    maxLevel: QILIN_PROGRESSION_CONFIG.maxLevel,
    stage: stageForLevel(level),
    name: pet.name || '',
    gender: pet.gender || '',
    xp,
    xpIntoLevel: isMaxLevel ? 0 : Math.max(0, xp - totalXpForLevel(level)),
    xpToNext: isMaxLevel ? 0 : xpToNext(level),
    isMaxLevel,
    bondXp,
    bondStars: Math.floor(bondXp / QILIN_PROGRESSION_CONFIG.bondStepXp),
    bondIntoStar: bondXp % QILIN_PROGRESSION_CONFIG.bondStepXp,
    bondStepXp: QILIN_PROGRESSION_CONFIG.bondStepXp,
    mood: moodFromLastFed(pet.lastFedDate || '', today),
    feedCount: Math.max(0, Number(pet.feedCount) || 0),
    lastFedDate: pet.lastFedDate || '',
    lastPlayedDate: pet.lastPlayedDate || '',
    playedToday: (pet.lastPlayedDate || '') === today,
    renameCost: QILIN_PROGRESSION_CONFIG.renameCost
  }
}
